// NotificationCenter.jsx - Centre de notifications in-app
// Icône cloche + dropdown avec notifications récentes
// Supporte: social-notifications ET client-notifications (compte rendu, chantiers)

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, Check, CheckCheck, Trash2, X, Settings,
  ChevronRight, Circle, ClipboardCheck, HardHat, FileText,
  Receipt, MessageSquare, Clock, Camera, Mic, Sparkles, Euro
} from 'lucide-react';

import { API_URL } from '../../config/api';

// Polling interval (30 seconds)
const POLL_INTERVAL = 30000;

// Notification type icons
const NOTIFICATION_ICONS = {
  compte_rendu: ClipboardCheck,
  chantier: HardHat,
  devis: FileText,
  facture: Receipt,
  message: MessageSquare,
  reminder: Clock,
  new_opportunity: Sparkles,
  devis_response: Euro,
  default: Bell
};

// Notification type colors
const NOTIFICATION_COLORS = {
  compte_rendu: 'text-orange-500 bg-orange-50',
  chantier: 'text-violet-500 bg-violet-50',
  devis: 'text-blue-500 bg-blue-50',
  facture: 'text-green-500 bg-green-50',
  message: 'text-pink-500 bg-pink-50',
  reminder: 'text-amber-500 bg-amber-50',
  new_opportunity: 'text-orange-500 bg-orange-50',
  devis_response: 'text-green-500 bg-green-50',
  default: 'text-slate-500 bg-slate-50'
};

// Format relative date
const formatRelativeDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `${diffMins} min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// Get auth token
const getAuthToken = () => {
  return localStorage.getItem('client_token') ||
         localStorage.getItem('ecosystem_token') || 
         localStorage.getItem('buyer_token') || 
         localStorage.getItem('user_token');
};

export default function NotificationCenter() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const token = getAuthToken();

  // Fetch unread count from both sources
  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    
    let totalUnread = 0;
    
    // Try client notifications first
    try {
      const clientResponse = await fetch(`${API_URL}/api/client-notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (clientResponse.ok) {
        const data = await clientResponse.json();
        totalUnread += data.unread_count || 0;
      }
    } catch (error) {
      // Ignore - API might not exist for this user type
    }
    
    // Also try social notifications
    try {
      const socialResponse = await fetch(`${API_URL}/api/social-notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (socialResponse.ok) {
        const data = await socialResponse.json();
        totalUnread += data.unread_count || 0;
      }
    } catch (error) {
      // Ignore
    }
    
    setUnreadCount(totalUnread);
  }, [token]);

  // Fetch full notifications from both sources
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    setIsLoading(true);
    let allNotifications = [];
    let totalUnread = 0;
    
    // Fetch client notifications (compte rendu, chantiers, etc.)
    try {
      const clientResponse = await fetch(`${API_URL}/api/client-notifications?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (clientResponse.ok) {
        const data = await clientResponse.json();
        allNotifications = [...allNotifications, ...data.notifications.map(n => ({
          ...n,
          source: 'client',
          is_read: n.read
        }))];
        totalUnread += data.unread_count || 0;
      }
    } catch (error) {
      console.log('Client notifications not available');
    }
    
    // Also fetch social notifications
    try {
      const socialResponse = await fetch(`${API_URL}/api/social-notifications?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (socialResponse.ok) {
        const data = await socialResponse.json();
        allNotifications = [...allNotifications, ...data.notifications.map(n => ({
          ...n,
          source: 'social'
        }))];
        totalUnread += data.unread_count || 0;
      }
    } catch (error) {
      console.log('Social notifications not available');
    }
    
    // Sort by date (newest first)
    allNotifications.sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );
    
    setNotifications(allNotifications.slice(0, 15));
    setUnreadCount(totalUnread);
    setIsLoading(false);
  }, [token]);

  // Mark single notification as read
  const markAsRead = async (notificationId, source = 'social') => {
    if (!token) return;
    
    const endpoint = source === 'client' 
      ? `${API_URL}/api/client-notifications/${notificationId}/read`
      : `${API_URL}/api/social-notifications/${notificationId}/read`;
    
    try {
      await fetch(endpoint, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    if (!token) return;
    
    // Mark all in both sources
    try {
      await Promise.all([
        fetch(`${API_URL}/api/client-notifications/read-all`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/social-notifications/read-all`, {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (e, notificationId, source = 'social') => {
    e.stopPropagation();
    if (!token) return;
    
    const endpoint = source === 'client'
      ? `${API_URL}/api/client-notifications/${notificationId}`
      : `${API_URL}/api/social-notifications/${notificationId}`;
    
    try {
      await fetch(endpoint, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const wasUnread = notifications.find(n => n.id === notificationId && !n.is_read && !n.read);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    const isUnread = !notification.is_read && !notification.read;
    if (isUnread) {
      markAsRead(notification.id, notification.source || 'social');
    }
    
    // Determine action URL
    let actionUrl = notification.action_url;
    
    // For compte rendu notifications, build the URL from data
    if (notification.type === 'compte_rendu' && notification.data) {
      const { chantier_id, compte_rendu_id } = notification.data;
      if (chantier_id) {
        actionUrl = `/espace-client/crm-batiment/chantiers/compte-rendu?chantier_id=${chantier_id}`;
      }
    }
    
    if (actionUrl) {
      setIsOpen(false);
      navigate(actionUrl);
    }
  };
  
  // Get icon for notification type
  const getNotificationIcon = (type) => {
    return NOTIFICATION_ICONS[type] || NOTIFICATION_ICONS.default;
  };
  
  // Get color classes for notification type
  const getNotificationColorClass = (type) => {
    return NOTIFICATION_COLORS[type] || NOTIFICATION_COLORS.default;
  };

  // Polling for unread count
  useEffect(() => {
    if (!token) return;
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);
    
    return () => clearInterval(interval);
  }, [token, fetchUnreadCount]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (isOpen && token) {
      fetchNotifications();
    }
  }, [isOpen, token, fetchNotifications]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if not logged in
  if (!token) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={22} className="text-gray-600" />
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span 
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white rounded-full px-1"
            style={{ backgroundColor: '#EF4444' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-[380px] max-h-[480px] bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden z-50"
            style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-[#1E3A5F]">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-[#F97316] hover:text-[#EA580C] font-medium flex items-center gap-1"
                  >
                    <CheckCheck size={14} />
                    Tout marquer lu
                  </button>
                )}
                <button
                  onClick={() => { setIsOpen(false); navigate('/notifications'); }}
                  className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                  title="Paramètres"
                >
                  <Settings size={16} className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto max-h-[360px]">
              {isLoading ? (
                <div className="py-8 text-center text-gray-400">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-[#F97316] rounded-full animate-spin mx-auto"></div>
                  <p className="text-sm mt-2">Chargement...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="py-12 text-center">
                  <Bell size={32} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500 font-medium">Aucune notification</p>
                  <p className="text-sm text-gray-400 mt-1">Tu seras notifié ici</p>
                </div>
              ) : (
                notifications.map((notification) => {
                  const IconComponent = getNotificationIcon(notification.type);
                  const colorClass = getNotificationColorClass(notification.type);
                  const isUnread = !notification.is_read && !notification.read;
                  
                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors group ${
                        isUnread ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        {/* Icon */}
                        <div 
                          className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}
                        >
                          <IconComponent size={18} />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm ${isUnread ? 'font-semibold text-[#1E3A5F]' : 'text-gray-700'}`}>
                              {notification.title}
                            </p>
                            {isUnread && (
                              <Circle size={8} className="flex-shrink-0 mt-1.5 fill-[#3B82F6] text-[#3B82F6]" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          
                          {/* Rich data for compte rendu */}
                          {notification.type === 'compte_rendu' && notification.data && (
                            <div className="flex items-center gap-2 mt-1.5">
                              {notification.data.has_photos && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                                  <Camera size={10} /> Photos
                                </span>
                              )}
                              {notification.data.has_voice_note && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                                  <Mic size={10} /> Audio
                                </span>
                              )}
                              {notification.data.checklist_progress && (
                                <span className="text-[10px] text-slate-500">
                                  ✓ {notification.data.checklist_progress}
                                </span>
                              )}
                            </div>
                          )}
                          
                          <p className="text-[10px] text-gray-400 mt-1">
                            {formatRelativeDate(notification.created_at)}
                          </p>
                        </div>
                        
                        {/* Delete button (visible on hover) */}
                        <button
                          onClick={(e) => deleteNotification(e, notification.id, notification.source)}
                          className="p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={() => { setIsOpen(false); navigate('/notifications'); }}
                  className="w-full text-center text-sm text-[#F97316] hover:text-[#EA580C] font-medium flex items-center justify-center gap-1"
                >
                  Voir toutes les notifications
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
